import React, { isValidElement, useEffect, useRef, useState } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { match } from 'ts-pattern';
import RNCarousel, { ICarouselInstance } from 'react-native-reanimated-carousel';
import { parallaxLayout, tinderLayout } from '../../utils/Carousel';
import { useSharedValue } from 'react-native-reanimated';
import AppText from './AppText';
import { DARK_GRAY, WHITE } from '../../styles/appColor';
import FontAwesome6 from 'react-native-vector-icons/FontAwesome6';

export type CarouselRenderItemProps<T> = {
  item: T;
  index: number;
  selected: boolean;
  width: number;
  height: number;
};

export type CarouselProps<T> = {
  data: T[];
  extraItems?: React.ReactElement[];
  mode: 'parallax' | 'tinder' | 'parallax-simple';
  width?: number;
  height?: number;
  displayImageNumber?: boolean;
  defaultIndex?: number;
  buttonsPosition?: 'far' | 'near' | 'inside';
  renderEmptyComponent?: ({ width, height }: { width: number; height: number }) => React.ReactElement;
  renderItem: (item: CarouselRenderItemProps<T>) => React.ReactElement;
};

export default function Carousel<T>({
  data,
  extraItems,
  mode,
  width = 150,
  height = 150,
  displayImageNumber = false,
  defaultIndex,
  buttonsPosition = 'far',
  renderEmptyComponent,
  renderItem,
}: CarouselProps<T>) {
  const carouselRef = useRef<null | ICarouselInstance>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lastLength, setLastLength] = useState<number | null>(null);
  const [rerender, setRerender] = useState(0);
  const directionAnimVal = useSharedValue(0);

  // Navigate to the new item if just added
  useEffect(() => {
    if (data.length > 0 && lastLength !== null && lastLength < data.length) {
      carouselRef.current?.scrollTo({ index: data.length - 1, count: data.length, animated: true });
      setCurrentIndex(data.length - 1);
      setLastLength(data.length);
    } else if (lastLength !== null && lastLength > data.length) {
      carouselRef.current?.scrollTo({ index: 0, count: data.length });
      setLastLength(data.length);
      setRerender((value) => value + 1);
      setCurrentIndex(0);
    }
  }, [data.length, lastLength]);

  useEffect(() => {
    if (data.length > 0 && lastLength === null) {
      setLastLength(data.length);
    }
  }, [data.length, lastLength]);

  if (!data || data.length === 0) {
    return <View style={{ width, height }}>{renderEmptyComponent && renderEmptyComponent({ width, height })}</View>;
  }

  if (data.length + (extraItems || []).length === 1) {
    return (
      <View style={{ width, height }}>{renderItem({ item: data[0], index: 0, selected: true, width, height })}</View>
    );
  }

  return (
    <View>
      <RNCarousel
        key={rerender}
        ref={carouselRef}
        style={styles.carousel}
        containerStyle={[styles.carouselContainer, { height }]}
        width={width}
        data={[...data, ...(extraItems || [])]}
        enabled={data.length > 1}
        defaultIndex={defaultIndex}
        renderItem={({ item, index }) => {
          if (isValidElement(item)) {
            return item;
          } else {
            return renderItem({
              item: item as T,
              index,
              selected: index === currentIndex,
              width,
              height,
            });
          }
        }}
        customAnimation={match(mode)
          .with('parallax', () =>
            parallaxLayout(
              {
                size: width,
                vertical: false,
              },
              {
                parallaxScrollingScale: 1,
                parallaxAdjacentItemScale: 0.5,
                parallaxScrollingOffset: 40,
              }
            )
          )
          .with('tinder', () =>
            tinderLayout({
              dataLength: data.length,
              pageWidth: width,
              directionAnimVal: directionAnimVal,
            })
          )
          .with('parallax-simple', () => undefined)
          .exhaustive()}
        mode={match(mode)
          .with('parallax', () => undefined)
          .with('tinder', () => undefined)
          .with('parallax-simple', () => 'parallax' as 'parallax')
          .exhaustive()}
        modeConfig={match(mode)
          .with('parallax', () => undefined)
          .with('tinder', () => undefined)
          .with('parallax-simple', () => ({
            parallaxScrollingScale: 0.9,
            parallaxScrollingOffset: 50,
          }))
          .exhaustive()}
        scrollAnimationDuration={400}
        onSnapToItem={setCurrentIndex}
        loop={false}
      />
      {displayImageNumber && (
        <AppText style={styles.imageNumber}>
          {currentIndex + 1}/{data.length}
        </AppText>
      )}
      {data.length > 1 && (
        <>
          <TouchableOpacity
            onPress={() => {
              setCurrentIndex((currentIndex) => currentIndex - 1);
              carouselRef.current?.prev();
            }}
            style={[
              styles.prevButton,
              { top: (height - 30) / 2 },
              match(buttonsPosition)
                .with('far', () => ({
                  left: -100,
                }))
                .with('near', () => ({
                  left: -34,
                }))
                .with('inside', () => styles.insideButton)
                .exhaustive(),
              currentIndex === 0 ? styles.disabled : undefined,
            ]}
            disabled={currentIndex === 0}
          >
            <FontAwesome6 name="chevron-left" color={DARK_GRAY} size={30} />
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => {
              setCurrentIndex((currentIndex) => currentIndex + 1);
              carouselRef.current?.next();
            }}
            style={[
              styles.nextButton,
              { top: (height - 30) / 2 },
              match(buttonsPosition)
                .with('far', () => ({
                  right: -100,
                }))
                .with('near', () => ({
                  right: -34,
                }))
                .with('inside', () => styles.insideButton)
                .exhaustive(),
              currentIndex === data.length - 1 ? styles.disabled : undefined,
            ]}
            disabled={currentIndex === data.length - 1}
          >
            <FontAwesome6 name="chevron-right" color={DARK_GRAY} size={30} />
          </TouchableOpacity>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  carousel: { overflow: 'visible' },
  carouselContainer: {
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 10,
  },
  imageNumber: {
    fontSize: 12,
    textAlign: 'center',
    color: DARK_GRAY,
  },
  insideButton: {
    backgroundColor: WHITE,
    borderRadius: 8,
    borderColor: DARK_GRAY,
    borderWidth: 1,
  },
  prevButton: {
    position: 'absolute',
    left: 30,
    zIndex: 10,
    width: 34,
    height: 34,
    justifyContent: 'center',
    alignItems: 'center',
  },
  nextButton: {
    position: 'absolute',
    right: 30,
    zIndex: 10,
    width: 34,
    height: 34,
    justifyContent: 'center',
    alignItems: 'center',
  },
  disabled: {
    opacity: 0.5,
  },
});
